import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Tooltip,
  withStyles
} from '@material-ui/core';
import {
  Close as CloseIcon,
  GetApp as ExportIcon,
  AttachFile as AttachFileIcon
} from '@material-ui/icons';
// Removed notistack dependency - enqueueSnackbar passed as prop

// GraphQL queries for drill-down data
const GET_DRILLDOWN_NOTES = gql`
  query GetQuickNotesDrilldown(
    $month: String
    $startDate: String
    $endDate: String
    $stack: QuickNotesStackEnum!
    $stackKey: String!
    $ownerId: ID
    $categoryId: ID
    $limit: Int
    $offset: Int
    $orderBy: String
    $order: String
  ) {
    getQuickNotesDrilldown(
      month: $month
      startDate: $startDate
      endDate: $endDate
      stack: $stack
      stackKey: $stackKey
      ownerId: $ownerId
      categoryId: $categoryId
      limit: $limit
      offset: $offset
      orderBy: $orderBy
      order: $order
    ) {
      id
      comment
      time_spent
      time_spent_formatted
      interaction_date
      created_at
      planned
      user {
        id
        name
      }
      commentable {
        ... on InventoryUser {
          id
          name
        }
      }
      activityType {
        id
        name
      }
      quickNoteCategory {
        id
        name
      }
      documents {
        id
        name
        file_size
        mime_type
        download_url
      }
    }
  }
`;

const GET_DRILLDOWN_COUNT = gql`
  query GetQuickNotesDrilldownCount(
    $month: String
    $startDate: String
    $endDate: String
    $stack: QuickNotesStackEnum!
    $stackKey: String!
    $ownerId: ID
    $categoryId: ID
  ) {
    getQuickNotesDrilldownCount(
      month: $month
      startDate: $startDate
      endDate: $endDate
      stack: $stack
      stackKey: $stackKey
      ownerId: $ownerId
      categoryId: $categoryId
    )
  }
`;

const styles = (theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '1200px',
    },
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1),
  },
  content: {
    padding: 0,
    '&:first-child': {
      paddingTop: 0,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
    padding: theme.spacing(4),
  },
  tableContainer: {
    maxHeight: '60vh',
    position: 'relative',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    backdropFilter: 'blur(2px)',
  },
  retryBanner: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #2196f3',
    borderRadius: '4px',
    padding: '8px 16px',
    margin: '8px 0',
    textAlign: 'center',
  },
  offlineBanner: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    color: '#856404',
    borderRadius: '4px',
    padding: '8px 16px',
    margin: '8px 0',
    textAlign: 'center',
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[50],
    '& .MuiTableCell-head': {
      fontWeight: 600,
      borderBottom: `2px solid ${theme.palette.divider}`,
      whiteSpace: 'nowrap',
    },
  },
  sortLabel: {
    '& .MuiTableSortLabel-icon': {
      fontSize: '1rem',
    },
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  commentCell: {
    maxWidth: 300,
    wordBreak: 'break-word',
  },
  commentText: {
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.4,
  },
  dateCell: {
    minWidth: 120,
  },
  ownerCell: {
    minWidth: 150,
  },
  inventoryUserCell: {
    minWidth: 150,
  },
  activityTypeCell: {
    minWidth: 130,
  },
  categoryCell: {
    minWidth: 120,
  },
  plannedCell: {
    minWidth: 100,
  },
  timeSpentCell: {
    minWidth: 90,
  },
  attachmentCell: {
    width: 80,
    textAlign: 'center',
  },
  plannedChip: {
    fontSize: '0.75rem',
    height: 24,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  actions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

const QuickNotesDrilldownModal = ({ 
  open, 
  onClose, 
  drilldownData, 
  onExport,
  classes,
  enqueueSnackbar
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('interaction_date');
  const [order, setOrder] = useState('desc');
  const [isChangingPage, setIsChangingPage] = useState(false);
  const [previousFocusElement, setPreviousFocusElement] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Extract drill-down parameters
  const {
    month,
    dateRange,
    startDate: directStartDate,
    endDate: directEndDate,
    stackDimension,
    stackKey,
    ownerId,
    ownerName,
    categoryId,
    categoryName,
    metric
  } = drilldownData || {};

  // Use dateRange if provided, otherwise use direct startDate/endDate or month
  const startDate = dateRange?.startDate || directStartDate;
  const endDate = dateRange?.endDate || directEndDate;
  

  // Validate drill-down data structure
  const validateDrilldownData = () => {
    if (!drilldownData) {
      console.warn('QuickNotesDrilldownModal: No drill-down data provided');
      return false;
    }

    // Required fields for all drill-downs
    if (!stackDimension || !stackKey) {
      console.warn('QuickNotesDrilldownModal: Missing required stackDimension or stackKey', drilldownData);
      return false;
    }

    // Date range validation
    if (!month && !startDate && !endDate) {
      console.warn('QuickNotesDrilldownModal: No date range specified', drilldownData);
      return false;
    }

    // Stack-specific validation (handle both uppercase and lowercase)
    const normalizedStackDimension = stackDimension?.toLowerCase();
    switch (normalizedStackDimension) {
      case 'activity_type':
        // Activity type drill-down - should have activity type name as stackKey
        break;
      case 'planned':
        // Planned status drill-down - stackKey should be 'Planned' or 'Unplanned'
        if (stackKey !== 'Planned' && stackKey !== 'Unplanned') {
          console.warn('QuickNotesDrilldownModal: Invalid planned status stackKey', stackKey);
          return false;
        }
        break;
      case 'category':
        // Category drill-down - should have category name as stackKey
        break;
      default:
        console.warn('QuickNotesDrilldownModal: Unknown stackDimension', stackDimension);
        return false;
    }

    return true;
  };

  const isValidDrilldown = validateDrilldownData();

  // Build precise filtering query variables
  const buildQueryVariables = () => {
    if (!isValidDrilldown) {
      return {};
    }

    const baseVariables = {
      stack: stackDimension?.toUpperCase(), // Ensure uppercase for GraphQL enum
      stackKey,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      orderBy,
      order
    };

    // Add date filtering - prioritize dateRange over month
    if (startDate && endDate) {
      baseVariables.startDate = startDate;
      baseVariables.endDate = endDate;
    } else if (month) {
      baseVariables.month = month;
    }

    // Add context-specific filtering for Owner and Category charts
    if (ownerId) {
      baseVariables.ownerId = ownerId;
    }
    
    if (categoryId) {
      baseVariables.categoryId = categoryId;
    }

    return baseVariables;
  };

  const buildCountVariables = () => {
    if (!isValidDrilldown) {
      return {};
    }

    const baseVariables = {
      stack: stackDimension?.toUpperCase(), // Ensure uppercase for GraphQL enum
      stackKey
    };

    // Add date filtering - prioritize dateRange over month
    if (startDate && endDate) {
      baseVariables.startDate = startDate;
      baseVariables.endDate = endDate;
    } else if (month) {
      baseVariables.month = month;
    }

    // Add context-specific filtering
    if (ownerId) {
      baseVariables.ownerId = ownerId;
    }
    
    if (categoryId) {
      baseVariables.categoryId = categoryId;
    }

    return baseVariables;
  };

  const queryVariables = buildQueryVariables();
  const countVariables = buildCountVariables();
  

  // GraphQL queries with enhanced error handling
  const { data: notesData, loading: notesLoading, error: notesError, refetch: refetchNotes } = useQuery(
    GET_DRILLDOWN_NOTES,
    {
      variables: queryVariables,
      skip: !open || !drilldownData || !isValidDrilldown,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      onError: (error) => {
        console.error('QuickNotesDrilldownModal: Notes query error', error, { 
          queryVariables, 
          drilldownData, 
          retryCount,
          networkError: error.networkError,
          graphQLErrors: error.graphQLErrors
        });
        
        setLastError({
          type: 'notes',
          error,
          timestamp: new Date(),
          retryCount
        });

        // Show user-friendly error message
        const errorMessage = getErrorMessage(error);
        enqueueSnackbar(`Error loading notes: ${errorMessage}`, { 
          variant: 'error',
          persist: error.networkError ? true : false
        });
      },
      onCompleted: () => {
        // Clear error state on successful load
        if (lastError?.type === 'notes') {
          setLastError(null);
        }
      }
    }
  );

  const { data: countData, loading: countLoading, error: countError, refetch: refetchCount } = useQuery(
    GET_DRILLDOWN_COUNT,
    {
      variables: countVariables,
      skip: !open || !drilldownData || !isValidDrilldown,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      onError: (error) => {
        console.error('QuickNotesDrilldownModal: Count query error', error, { 
          countVariables, 
          drilldownData,
          retryCount,
          networkError: error.networkError,
          graphQLErrors: error.graphQLErrors
        });
        
        setLastError({
          type: 'count',
          error,
          timestamp: new Date(),
          retryCount
        });
      },
      onCompleted: () => {
        // Clear error state on successful load
        if (lastError?.type === 'count') {
          setLastError(null);
        }
      }
    }
  );

  // Focus management for modal accessibility
  useEffect(() => {
    if (open) {
      // Store the previously focused element
      setPreviousFocusElement(document.activeElement);
      
      // Focus the modal after a short delay to ensure it's rendered
      setTimeout(() => {
        const modalTitle = document.getElementById('drilldown-modal-title');
        if (modalTitle) {
          modalTitle.focus();
        }
      }, 100);
    } else if (previousFocusElement) {
      // Restore focus to the previously focused element when modal closes
      setTimeout(() => {
        if (previousFocusElement && previousFocusElement.focus) {
          previousFocusElement.focus();
        }
      }, 100);
    }
  }, [open]);

  // Reset pagination when drill-down data changes
  useEffect(() => {
    if (open && drilldownData) {
      setPage(0);
      setIsChangingPage(false);
    }
  }, [open, drilldownData]);

  // Comprehensive keyboard accessibility and modal management
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open) return;
      
      // Handle ESC key for modal close (always available)
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
        return;
      }
      
      // Check if an input/interactive element is focused
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'SELECT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.contentEditable === 'true' ||
        activeElement.role === 'button' ||
        activeElement.role === 'tab' ||
        activeElement.tagName === 'BUTTON'
      );
      
      // Don't interfere with input interactions, except for ESC
      if (isInputFocused && event.key !== 'Escape') return;

      // Pagination keyboard shortcuts
      switch (event.key) {
        case 'ArrowLeft':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            handleFirstPage();
          } else {
            event.preventDefault();
            handlePreviousPage();
          }
          break;
        case 'ArrowRight':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            handleLastPage();
          } else {
            event.preventDefault();
            handleNextPage();
          }
          break;
        case 'Home':
          if (!event.ctrlKey && !event.metaKey) {
            event.preventDefault();
            handleFirstPage();
          }
          break;
        case 'End':
          if (!event.ctrlKey && !event.metaKey) {
            event.preventDefault();
            handleLastPage();
          }
          break;
        case 'PageUp':
          event.preventDefault();
          handlePreviousPage();
          break;
        case 'PageDown':
          event.preventDefault();
          handleNextPage();
          break;
        // Export shortcut
        case 'e':
          if ((event.ctrlKey || event.metaKey) && onExport && totalCount > 0 && isValidDrilldown) {
            event.preventDefault();
            handleExport();
          }
          break;
        // Refresh/reload data
        case 'r':
          if ((event.ctrlKey || event.metaKey) && refetchNotes) {
            event.preventDefault();
            refetchNotes();
          }
          break;
        // Focus close button
        case 'c':
          if (event.altKey) {
            event.preventDefault();
            const closeButton = document.querySelector('[aria-label="close"]');
            if (closeButton) closeButton.focus();
          }
          break;
        default:
          break;
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, page, totalCount, rowsPerPage, onExport, isValidDrilldown, handleClose]);

  // Refetch when pagination or sorting changes
  useEffect(() => {
    if (open && drilldownData && refetchNotes) {
      refetchNotes(queryVariables);
    }
  }, [page, rowsPerPage, orderBy, order]);

  const handleChangePage = (event, newPage) => {
    setIsChangingPage(true);
    setPage(newPage);
    
    // Clear loading state after a brief delay to show user feedback
    setTimeout(() => setIsChangingPage(false), 100);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setIsChangingPage(true);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
    
    // Clear loading state after a brief delay
    setTimeout(() => setIsChangingPage(false), 100);
  };

  const handleFirstPage = () => {
    if (page !== 0) {
      handleChangePage(null, 0);
    }
  };

  const handleLastPage = () => {
    const lastPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page !== lastPage) {
      handleChangePage(null, lastPage);
    }
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      handleChangePage(null, page - 1);
    }
  };

  const handleNextPage = () => {
    const lastPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    if (page < lastPage) {
      handleChangePage(null, page + 1);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleExport = () => {
    if (onExport && isValidDrilldown) {
      onExport({
        ...drilldownData,
        totalCount: countData?.getQuickNotesDrilldownCount || 0,
        queryVariables,
        isValidDrilldown
      });
      enqueueSnackbar('Export started...', { variant: 'info' });
    } else if (!isValidDrilldown) {
      enqueueSnackbar('Cannot export: Invalid drill-down data', { variant: 'error' });
    }
  };

  const handleClose = useCallback(() => {
    // Clear any loading states
    setIsChangingPage(false);
    
    // Clear error states
    setLastError(null);
    setRetryCount(0);
    
    // Call the onClose callback
    onClose();
  }, [onClose]);

  // Helper function to get user-friendly error messages
  const getErrorMessage = (error) => {
    if (error.networkError) {
      if (error.networkError.statusCode === 404) {
        return 'Data not found. The requested information may no longer be available.';
      } else if (error.networkError.statusCode === 403) {
        return 'Access denied. You may not have permission to view this data.';
      } else if (error.networkError.statusCode === 500) {
        return 'Server error. Please try again in a few moments.';
      } else if (!navigator.onLine) {
        return 'No internet connection. Please check your network.';
      } else {
        return 'Network error. Please check your connection and try again.';
      }
    } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      const graphQLError = error.graphQLErrors[0];
      if (graphQLError.extensions?.code === 'UNAUTHENTICATED') {
        return 'Session expired. Please refresh the page and log in again.';
      } else if (graphQLError.extensions?.code === 'FORBIDDEN') {
        return 'Access denied. You may not have permission to view this data.';
      } else {
        return graphQLError.message || 'Server error occurred.';
      }
    } else {
      return error.message || 'An unexpected error occurred.';
    }
  };

  // Retry function with exponential backoff
  const handleRetry = useCallback(() => {
    const newRetryCount = retryCount + 1;
    setRetryCount(newRetryCount);
    setLastError(null);
    
    // Show retry feedback
    enqueueSnackbar(`Retrying... (Attempt ${newRetryCount})`, { variant: 'info' });
    
    // Retry both queries
    if (refetchNotes) {
      refetchNotes();
    }
    if (refetchCount) {
      refetchCount();
    }
    
    // Exponential backoff for future retries
    setTimeout(() => {
      setIsChangingPage(false);
    }, Math.min(1000 * Math.pow(2, newRetryCount - 1), 5000));
  }, [retryCount, refetchNotes, refetchCount, enqueueSnackbar]);

  // Clean category name for display
  const cleanCategoryName = (categoryName) => {
    if (!categoryName) return categoryName;
    return categoryName
      .replace('Practice Exchange   ', '')
      .replace('Succession Planning   ', '');
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format datetime for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Table column definitions
  const columns = [
    {
      id: 'interaction_date',
      label: 'Interaction Date',
      sortable: true,
      align: 'left',
      format: (value) => formatDate(value),
      tooltip: 'The date when the activity occurred',
      className: classes.dateCell
    },
    {
      id: 'created_at',
      label: 'Created',
      sortable: true,
      align: 'left',
      format: (value) => formatDateTime(value),
      tooltip: 'When this note was created in the system',
      className: classes.dateCell
    },
    {
      id: 'owner',
      label: 'Owner',
      sortable: true,
      align: 'left',
      format: (note) => note.user?.name || 'Unknown',
      tooltip: 'The person who created this note',
      className: classes.ownerCell
    },
    {
      id: 'inventory_user',
      label: 'Inventory User',
      sortable: true,
      align: 'left',
      format: (note) => note.commentable?.name || 'Unknown',
      tooltip: 'The person this note is about',
      className: classes.inventoryUserCell
    },
    {
      id: 'activity_type',
      label: 'Activity Type',
      sortable: true,
      align: 'left',
      format: (note) => note.activityType?.name || 'Unknown',
      tooltip: 'The type of activity performed',
      className: classes.activityTypeCell
    },
    {
      id: 'category',
      label: 'Category',
      sortable: true,
      align: 'left',
      format: (note) => cleanCategoryName(note.quickNoteCategory?.name) || 'Unknown',
      tooltip: 'The Quick Note category',
      className: classes.categoryCell
    },
    {
      id: 'planned',
      label: 'Planned',
      sortable: true,
      align: 'center',
      format: (note) => note.planned,
      tooltip: 'Whether this activity was planned or unplanned',
      className: classes.plannedCell
    },
    {
      id: 'time_spent',
      label: 'Time Spent',
      sortable: true,
      align: 'right',
      format: (note) => ({
        formatted: note.time_spent_formatted || '0m',
        minutes: note.time_spent || 0
      }),
      tooltip: 'Time spent on this activity',
      className: classes.timeSpentCell
    },
    {
      id: 'attachments',
      label: 'Attachment',
      sortable: false,
      align: 'center',
      format: (note) => note.documents && note.documents.length > 0,
      tooltip: 'Has file attachments',
      className: classes.attachmentCell
    },
    {
      id: 'comment',
      label: 'Comment',
      sortable: false,
      align: 'left',
      format: (note) => note.comment || 'No comment',
      tooltip: 'Additional notes or comments',
      className: classes.commentCell
    }
  ];

  // Generate modal title with comprehensive filtering context
  const getModalTitle = () => {
    if (!isValidDrilldown) {
      return 'Quick Notes: Invalid drill-down data';
    }

    const parts = [];
    
    // Add stack key (the specific segment clicked)
    if (stackKey) {
      parts.push(`"${stackKey}"`);
    }
    
    // Add context-specific information
    if (ownerName) {
      parts.push(`by ${ownerName}`);
    }
    
    if (categoryName) {
      parts.push(`in ${cleanCategoryName(categoryName)}`);
    }
    
    // Add date context
    if (month) {
      const monthLabel = new Date(month + '-01').toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
      parts.push(`for ${monthLabel}`);
    } else if (startDate && endDate) {
      const startLabel = new Date(startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      const endLabel = new Date(endDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      parts.push(`from ${startLabel} to ${endLabel}`);
    }
    
    // Add stack dimension context for clarity
    let dimensionContext = '';
    switch (stackDimension) {
      case 'activity_type':
        dimensionContext = '(Activity Type)';
        break;
      case 'planned':
        dimensionContext = '(Planned Status)';
        break;
      case 'category':
        dimensionContext = '(Category)';
        break;
    }
    
    if (dimensionContext) {
      parts.push(dimensionContext);
    }
    
    return `Quick Notes: ${parts.join(' ')}`;
  };

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (lastError && lastError.error?.networkError) {
        enqueueSnackbar('Connection restored. Data will be refreshed.', { variant: 'success' });
        // Auto-retry when coming back online
        setTimeout(() => {
          if (refetchNotes) refetchNotes();
          if (refetchCount) refetchCount();
        }, 1000);
      }
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      enqueueSnackbar('Connection lost. Some features may not work.', { 
        variant: 'warning',
        persist: true 
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [lastError, refetchNotes, refetchCount, enqueueSnackbar]);

  const notes = notesData?.getQuickNotesDrilldown || [];
  const totalCount = countData?.getQuickNotesDrilldownCount || 0;
  const loading = notesLoading || countLoading;
  
  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startIndex = totalCount === 0 ? 0 : (page * rowsPerPage) + 1;
  const endIndex = Math.min((page + 1) * rowsPerPage, totalCount);
  const hasNextPage = page < totalPages - 1;
  const hasPreviousPage = page > 0;

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      aria-labelledby="drilldown-modal-title"
      aria-describedby="drilldown-modal-description"
      aria-modal="true"
      role="dialog"
      disableBackdropClick={false}
      disableEscapeKeyDown={false}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        'aria-label': 'Quick Notes drill-down details'
      }}
    >
      <DialogTitle id="drilldown-modal-title" className={classes.title}>
        <Typography 
          variant="h6" 
          component="h2"
          tabIndex={-1}
          style={{ outline: 'none' }}
        >
          {getModalTitle()}
        </Typography>
        <IconButton
          aria-label="close modal"
          onClick={handleClose}
          size="small"
          title="Close (Esc)"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {/* Hidden description for screen readers */}
        <div id="drilldown-modal-description" style={{ position: 'absolute', left: '-10000px' }}>
          Quick Notes drill-down table showing detailed notes data for the selected chart segment. 
          Use arrow keys to navigate pages, Escape to close, or tab to navigate through table controls.
        </div>
        
        {!isValidDrilldown ? (
          <div className={classes.emptyState}>
            <Typography color="error" variant="h6">
              Invalid Drill-Down Data
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              The drill-down request is missing required information. Please try clicking on a chart segment again.
            </Typography>
            {process.env.NODE_ENV === 'development' && (
              <Typography variant="caption" style={{ marginTop: 16, fontFamily: 'monospace' }}>
                Debug: {JSON.stringify(drilldownData, null, 2)}
              </Typography>
            )}
          </div>
        ) : loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={60} />
            <Typography variant="body1" style={{ marginTop: 16, textAlign: 'center' }}>
              {countLoading && notesLoading ? 'Loading drill-down data...' : 
               countLoading ? 'Counting records...' : 
               notesLoading ? 'Loading notes...' : 'Loading...'}
            </Typography>
            {retryCount > 0 && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, textAlign: 'center' }}>
                Retry attempt {retryCount}
              </Typography>
            )}
          </div>
        ) : (notesError && !notesData) || (countError && !countData) ? (
          <div className={classes.emptyState}>
            <Typography color="error" variant="h6">
              Error Loading Data
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {getErrorMessage(notesError || countError)}
            </Typography>
            
            {/* Connection status */}
            {!isOnline && (
              <Box style={{ marginTop: 12, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary" style={{ 
                  backgroundColor: '#fff3cd', 
                  color: '#856404', 
                  padding: '8px 12px', 
                  borderRadius: '4px',
                  display: 'inline-block'
                }}>
                  ⚠️ You are currently offline
                </Typography>
              </Box>
            )}

            {/* Retry button */}
            <Box style={{ marginTop: 16, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleRetry}
                disabled={loading || (!isOnline && lastError?.error?.networkError)}
                style={{ marginRight: 8 }}
              >
                {loading ? 'Retrying...' : 
                 !isOnline && lastError?.error?.networkError ? 'Offline' : 
                 'Retry'}
              </Button>
              <Button
                variant="text"
                onClick={handleClose}
              >
                Close
              </Button>
            </Box>
            
            {retryCount > 0 && (
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, textAlign: 'center' }}>
                Retry attempts: {retryCount}
              </Typography>
            )}
            
            {process.env.NODE_ENV === 'development' && (
              <Box style={{ marginTop: 16, textAlign: 'left' }}>
                <Typography variant="caption" style={{ fontFamily: 'monospace', display: 'block' }}>
                  Error Details: {JSON.stringify(lastError, null, 2)}
                </Typography>
                <Typography variant="caption" style={{ fontFamily: 'monospace', display: 'block', marginTop: 8 }}>
                  Query: {JSON.stringify(queryVariables, null, 2)}
                </Typography>
              </Box>
            )}
          </div>
        ) : notes.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="body1">
              No notes found matching the selected criteria.
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              This segment may not contain any Quick Notes data for the specified filters.
            </Typography>
            {process.env.NODE_ENV === 'development' && (
              <Typography variant="caption" style={{ marginTop: 16, fontFamily: 'monospace' }}>
                Filters: {JSON.stringify({ stackDimension, stackKey, month, ownerId, categoryId }, null, 2)}
              </Typography>
            )}
          </div>
        ) : (
          <>
            <TableContainer component={Paper} className={classes.tableContainer}>
              {(loading || isChangingPage) && (
                <div className={classes.loadingOverlay}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" style={{ marginTop: 12 }}>
                    {isChangingPage ? 'Loading page...' :
                     countLoading && notesLoading ? 'Loading data...' :
                     countLoading ? 'Counting records...' :
                     notesLoading ? 'Loading notes...' : 'Loading...'}
                  </Typography>
                  {retryCount > 0 && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                      Retry attempt {retryCount}
                    </Typography>
                  )}
                  {!isOnline && (
                    <div className={classes.offlineBanner} style={{ marginTop: 12 }}>
                      <Typography variant="caption">
                        ⚠️ Offline mode - limited functionality
                      </Typography>
                    </div>
                  )}
                </div>
              )}
              <Table stickyHeader aria-label="drill-down notes table">
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell 
                        key={column.id} 
                        align={column.align}
                        sortDirection={orderBy === column.id ? order : false}
                        className={column.className}
                      >
                        {column.sortable ? (
                          <Tooltip title={column.tooltip} placement="top">
                            <TableSortLabel
                              active={orderBy === column.id}
                              direction={orderBy === column.id ? order : 'asc'}
                              onClick={() => handleRequestSort(column.id)}
                              className={classes.sortLabel}
                            >
                              {column.label}
                            </TableSortLabel>
                          </Tooltip>
                        ) : (
                          <Tooltip title={column.tooltip} placement="top">
                            <span>{column.label}</span>
                          </Tooltip>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notes.map((note) => (
                    <TableRow key={note.id} className={classes.tableRow}>
                      {columns.map((column) => {
                        let cellContent;
                        let cellProps = { 
                          key: column.id, 
                          align: column.align,
                          className: column.className
                        };

                        switch (column.id) {
                          case 'interaction_date':
                            cellContent = column.format(note.interaction_date);
                            break;
                          case 'created_at':
                            cellContent = column.format(note.created_at);
                            break;
                          case 'owner':
                            cellContent = column.format(note);
                            break;
                          case 'inventory_user':
                            cellContent = column.format(note);
                            break;
                          case 'activity_type':
                            cellContent = column.format(note);
                            break;
                          case 'category':
                            cellContent = column.format(note);
                            break;
                          case 'planned':
                            cellContent = (
                              <Chip
                                label={note.planned ? 'Planned' : 'Unplanned'}
                                color={note.planned ? 'primary' : 'default'}
                                variant={note.planned ? 'default' : 'outlined'}
                                size="small"
                                className={classes.plannedChip}
                              />
                            );
                            break;
                          case 'time_spent':
                            const timeData = column.format(note);
                            cellContent = (
                              <Tooltip title={`${timeData.minutes} minutes`}>
                                <span>{timeData.formatted}</span>
                              </Tooltip>
                            );
                            break;
                          case 'attachments':
                            cellContent = column.format(note) ? (
                              <AttachFileIcon color="action" fontSize="small" />
                            ) : null;
                            break;
                          case 'comment':
                            cellContent = (
                              <Tooltip title={column.format(note)} placement="top-start">
                                <div className={classes.commentText}>
                                  {column.format(note)}
                                </div>
                              </Tooltip>
                            );
                            // Keep the existing commentCell class for this column
                            break;
                          default:
                            cellContent = 'Unknown';
                        }

                        return (
                          <TableCell {...cellProps}>
                            {cellContent}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              backIconButtonProps={{
                'aria-label': 'Previous page',
                disabled: !hasPreviousPage || loading || isChangingPage,
                title: 'Previous page (←)'
              }}
              nextIconButtonProps={{
                'aria-label': 'Next page', 
                disabled: !hasNextPage || loading || isChangingPage,
                title: 'Next page (→)'
              }}
              SelectProps={{
                disabled: loading || isChangingPage,
                'aria-label': 'Rows per page'
              }}
              aria-label="drill-down notes pagination"
            />
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="body2" color="textSecondary">
            {totalCount} {totalCount === 1 ? 'note' : 'notes'} found
            {totalCount > 0 && (
              <span style={{ marginLeft: 8 }}>
                (Page {page + 1} of {totalPages})
              </span>
            )}
          </Typography>
          {totalCount > rowsPerPage && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
              Keyboard: ← → navigate, Ctrl+← Ctrl+→ first/last, PgUp/PgDn page, Esc close
              {onExport && isValidDrilldown && ', Ctrl+E export'}
            </Typography>
          )}
        </Box>
        <Box>
          {onExport && totalCount > 0 && isValidDrilldown && (
            <Button
              startIcon={<ExportIcon />}
              onClick={handleExport}
              variant="outlined"
              style={{ marginRight: 8 }}
              disabled={!isValidDrilldown}
            >
              Export CSV
            </Button>
          )}
          <Button 
            onClick={handleClose} 
            color="primary" 
            variant="contained"
            title="Close modal (Esc)"
          >
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

QuickNotesDrilldownModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  drilldownData: PropTypes.shape({
    month: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    stackDimension: PropTypes.string.isRequired,
    stackKey: PropTypes.string.isRequired,
    ownerId: PropTypes.string,
    ownerName: PropTypes.string,
    categoryId: PropTypes.string,
    categoryName: PropTypes.string,
    metric: PropTypes.string
  }),
  onExport: PropTypes.func,
  classes: PropTypes.object.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
};

export default withStyles(styles)(QuickNotesDrilldownModal);